import { X, Minus, Plus, Trash2 } from 'lucide-react';
import { useCart } from '@/context/CartContext';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';

const CartDrawer = () => {
  const { cart, isCartOpen, setIsCartOpen, removeFromCart, updateQuantity, getCartTotal } = useCart();
  const navigate = useNavigate();

  const handleCheckout = () => {
    setIsCartOpen(false);
    navigate('/checkout');
  };

  return (
    <AnimatePresence>
      {isCartOpen && (
        <>
          {/* Overlay */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setIsCartOpen(false)}
            className="fixed inset-0 bg-black/80 z-40"
            data-testid="cart-overlay"
          />

          {/* Drawer */}
          <motion.div
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', damping: 25, stiffness: 200 }}
            className="cart-drawer"
            data-testid="cart-drawer"
          >
            <div className="flex flex-col h-full">
              {/* Header */}
              <div className="flex justify-between items-center p-6 border-b border-white/10">
                <h2 className="font-heading text-xl font-bold uppercase" data-testid="cart-title">
                  Seu Carrinho
                </h2>
                <button
                  onClick={() => setIsCartOpen(false)}
                  className="p-2 hover:bg-white/5 rounded transition-colors"
                  data-testid="close-cart-button"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>

              {/* Cart Items */}
              <div className="flex-1 overflow-y-auto p-6" data-testid="cart-items-container">
                {cart.length === 0 ? (
                  <div className="text-center py-12" data-testid="empty-cart-message">
                    <p className="text-zinc-400">Seu carrinho está vazio</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {cart.map((item) => (
                      <motion.div
                        key={item.cart_id}
                        layout
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, x: -100 }}
                        className="flex gap-4 bg-zinc-900/50 p-4 border border-white/5"
                        data-testid={`cart-item-${item.cart_id}`}
                      >
                        <img
                          src={item.product.image}
                          alt={item.product.name}
                          className="w-20 h-20 object-cover"
                          data-testid="cart-item-image"
                        />
                        <div className="flex-1">
                          <h3 className="font-heading text-sm font-bold" data-testid="cart-item-name">
                            {item.product.name}
                          </h3>
                          <p className="text-xs text-zinc-400 mt-1" data-testid="cart-item-size">
                            Tamanho: {item.size}
                          </p>
                          <p className="font-mono text-sm text-primary mt-2" data-testid="cart-item-price">
                            R$ {item.product.price.toFixed(2)}
                          </p>

                          {/* Quantity Controls */}
                          <div className="flex items-center gap-2 mt-3">
                            <button
                              onClick={() => {
                                if (item.quantity > 1) {
                                  updateQuantity(item.cart_id, item.quantity - 1);
                                }
                              }}
                              className="p-1 hover:bg-white/5 transition-colors"
                              data-testid="decrease-quantity-button"
                            >
                              <Minus className="h-4 w-4" />
                            </button>
                            <span className="font-mono text-sm w-8 text-center" data-testid="cart-item-quantity">
                              {item.quantity}
                            </span>
                            <button
                              onClick={() => updateQuantity(item.cart_id, item.quantity + 1)}
                              className="p-1 hover:bg-white/5 transition-colors"
                              data-testid="increase-quantity-button"
                            >
                              <Plus className="h-4 w-4" />
                            </button>
                          </div>
                        </div>

                        <button
                          onClick={() => removeFromCart(item.cart_id)}
                          className="p-2 hover:bg-red-500/10 hover:text-red-500 transition-colors self-start"
                          data-testid="remove-item-button"
                        >
                          <Trash2 className="h-5 w-5" />
                        </button>
                      </motion.div>
                    ))}
                  </div>
                )}
              </div>

              {/* Footer */}
              {cart.length > 0 && (
                <div className="border-t border-white/10 p-6 space-y-4" data-testid="cart-footer">
                  <div className="flex justify-between items-center">
                    <span className="text-lg uppercase tracking-wider">Total</span>
                    <span className="font-mono text-2xl text-primary font-bold" data-testid="cart-total">
                      R$ {getCartTotal().toFixed(2)}
                    </span>
                  </div>
                  <button
                    onClick={handleCheckout}
                    className="w-full bg-primary text-black font-bold uppercase tracking-wider py-4 hover:bg-white transition-colors"
                    data-testid="checkout-button"
                  >
                    Finalizar Pedido
                  </button>
                </div>
              )}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default CartDrawer;